<template>
  <OpportunityAreaLearnMore :isOpen="isLearnMoreOpen" @closeModal="closeLearnMore" />

  <div v-if="isSectionVisible" class="tw:pb-6 tw:mb-6 tw:border-b tw:border-gray-300">
    <div>
      <h1 class="tw:text-black tw:font-bold tw:text-2xl tw:mb-3">
        {{ $t('rc_smartboost_opportunities_header') }}
      </h1>

      <p class="tw:mb-4">
        {{ $t('rc_smartboost_opportunities_subtext') }}
        <button @click="openLearnMore" class="tw:text-blue-700 tw:underline">
          {{ $t('rc_opportunities_learn_more') }}
        </button>
      </p>
    </div>

    <div
      v-if="showNoOpportunitiesBanner"
      class="tw:flex tw:items-start tw:gap-2 tw:bg-blue-200 tw:rounded-lg tw:px-4 tw:py-4 tw:my-8 tw:text-sm">
      <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:h-5 tw:w-5 tw:ml-1" />
      <p class="tw:mb-0" v-html="$t('smartboost_no_opportunity')" />
    </div>

    <OpportunityBoostOpportunitiesCarousel v-if="boostOpportunitiesCards.length > 0" :cards="boostOpportunitiesCards" />

    <div v-if="boostOpportunitiesChartData.length > 0">
      <div
        class="tw:flex tw:flex-col tw:md:flex-row tw:md:justify-between tw:md:items-start tw:gap-6 tw:mb-4 tw:md:mb-8">
        <div>
          <h2 class="tw:text-xl tw:font-bold tw:mb-2 tw:md:mb-2">
            {{ $t('rc_hotel_location', { HotelLocation: hotelLocation }) }}
          </h2>
          <p class="tw:mb-0">{{ $t('rc_opportuntities_demand_header') }}</p>
        </div>

        <OpportunityBoostChartDateFilter
          :start-date="windowStartDate"
          :end-date="windowEndDate"
          :can-go-previous="canGoPrevious"
          :can-go-next="canGoNext"
          @previous="goToPreviousWindow"
          @next="goToNextWindow" />
      </div>

      <OpportunityBoostOpportunitiesChart :chartData="visibleChartData" :cards="boostOpportunitiesCards" />
    </div>

    <p class="tw:text-xs tw:mt-4">{{ $t('rc_smartboost_opportunities_footnote') }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import OpportunityBoostChartDateFilter from '@/components/rateConnect/opportunityArea/boostOpportunities/OpportunityBoostChartDateFilter.vue'
import OpportunityBoostOpportunitiesCarousel from '@/components/rateConnect/opportunityArea/boostOpportunities/OpportunityBoostOpportunitiesCarousel.vue'
import OpportunityBoostOpportunitiesChart from '@/components/rateConnect/opportunityArea/boostOpportunities/OpportunityBoostOpportunitiesChart.vue'
import OpportunityAreaLearnMore from '@/components/rateConnect/opportunityArea/commissionLevel/OpportunityAreaLearnMore.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import { useBoostOpportunityChartWindow } from '@/composables/useBoostOpportunityChartWindow'
import { useBoostOpportunities } from '@/components/rateConnect/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'

const { boostOpportunitiesCards, boostOpportunitiesChartData } = useBoostOpportunities(selectedAccommodationId)
const { selectedOrganisationId } = useSessionValues()
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const hotelLocation = computed(() => currentAccommodation.value?.city || '')
const isLearnMoreOpen = ref(false)

const {
  visibleChartData,
  windowStartDate,
  windowEndDate,
  canGoPrevious,
  canGoNext,
  goToPreviousWindow,
  goToNextWindow,
} = useBoostOpportunityChartWindow(boostOpportunitiesChartData)

const openLearnMore = () => {
  isLearnMoreOpen.value = true
}

const closeLearnMore = () => {
  isLearnMoreOpen.value = false
}

const isSectionVisible = computed(() => {
  return boostOpportunitiesCards.value.length > 0 || boostOpportunitiesChartData.value.length > 0
})

const showNoOpportunitiesBanner = computed(() => {
  return boostOpportunitiesCards.value.length === 0 && boostOpportunitiesChartData.value.length > 0
})
</script>
